import React from 'react';

import { Badge } from '../../ui/badge';
import { Eye, Download, Trash2, Printer, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getFileTypeInfo, canPreviewInline, getViewUrl } from './constants';

const DocumentCard = ({ doc, categories: categories, onView, onDownload, onPrint, onDelete, formatFileSize, formatDate, getStatusBadge, printingDocId }) => {
  const fileInfo = getFileTypeInfo(doc.original_filename || doc.filename, doc.mime_type);
  const FileIcon = fileInfo.icon;
  const previewable = canPreviewInline(doc.original_filename || doc.filename, doc.mime_type);

  return (
    <div className={cn("group relative border rounded-xl overflow-hidden bg-white hover:shadow-md transition-all", fileInfo.borderColor)} data-testid={`doc-card-${doc.id}`}>
      {/* Thumbnail / Type header */}
      <div className={cn("h-28 flex items-center justify-center relative", fileInfo.bg)} onClick={() => onView(doc)} style={{ cursor: 'pointer' }}>
        {fileInfo.type === 'image' ? (
          <img
            src={getViewUrl(doc.id)}
            alt={doc.original_filename}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={cn("flex flex-col items-center justify-center", fileInfo.type === 'image' && "hidden")} style={fileInfo.type === 'image' ? { display: 'none' } : {}}>
          <FileIcon className={cn("w-10 h-10", fileInfo.color)} />
          <Badge variant="outline" className={cn("mt-2 text-[10px]", fileInfo.borderColor, fileInfo.color)}>{fileInfo.label}</Badge>
        </div>
        {previewable && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow">
              <Eye className="w-5 h-5 text-gray-700" />
            </div>
          </div>
        )}
        {/* Status badge */}
        <div className="absolute top-2 right-2">
          {getStatusBadge(doc.statut)}
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="font-medium text-sm text-gray-900 truncate" title={doc.original_filename || doc.nom}>
          {doc.original_filename || doc.nom}
        </p>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
          <span>{formatFileSize(doc.file_size)}</span>
          <span className="text-gray-300">|</span>
          <span>{formatDate(doc.uploaded_at)}</span>
        </div>
        {(doc.user_prenom || doc.user_nom) && (
          <p className="text-[11px] text-gray-500 mt-0.5 truncate">
            Par {[doc.user_prenom, doc.user_nom].filter(Boolean).join(' ')}
          </p>
        )}
      </div>

      {/* Actions bar */}
      <div className="flex items-center border-t border-gray-100 divide-x divide-gray-100">
        <button onClick={() => onView(doc)} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-blue-600 hover:bg-blue-50 transition-colors" data-testid={`grid-view-btn-${doc.id}`}>
          <Eye className="w-3.5 h-3.5" /> Voir
        </button>
        {onPrint && (
          <button onClick={() => onPrint(doc)} disabled={printingDocId === doc.id} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-50" data-testid={`grid-print-btn-${doc.id}`}>
            {printingDocId === doc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Imprimer
          </button>
        )}
        <button onClick={() => onDownload(doc)} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-emerald-600 hover:bg-emerald-50 transition-colors" data-testid={`grid-download-btn-${doc.id}`}>
          <Download className="w-3.5 h-3.5" /> Telecharger
        </button>
        <button onClick={() => onDelete(doc.id)} className="px-3 py-2 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`grid-delete-btn-${doc.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// === DOCUMENT LIST/GRID WRAPPER ===

export default DocumentCard;
